/**
 * Unified Advice Module Type System
 *
 * This file consolidates all module-related types across the application
 * to prevent type inconsistencies and provide a single source of truth.
 */

// =============================================================================
// CORE MODULE DEFINITIONS
// =============================================================================

/**
 * All advice modules available in the platform
 */
export const ALL_ADVICE_MODULES = [
  'superInvestments',
  'salarySacrificeContributions',
  'lumpSumConcessionalContributions',
  'lumpSumNonConcessionalContributions',
  'personalInsurance',
  'debtPlan',
  'retirementPlan'
] as const

/**
 * Main advice module type - covers all modules in the platform
 */
export type AdviceModuleType = typeof ALL_ADVICE_MODULES[number]

// =============================================================================
// MODULE CATEGORIZATION
// =============================================================================

/**
 * Free modules that don't require premium subscription
 */
export const FREE_ADVICE_MODULES = [
  'superInvestments'
] as const

export type FreeAdviceModuleType = typeof FREE_ADVICE_MODULES[number]

/**
 * Premium modules that require subscription for full access
 */
export const PREMIUM_ADVICE_MODULES = [
  'salarySacrificeContributions',
  'lumpSumConcessionalContributions',
  'lumpSumNonConcessionalContributions',
  'personalInsurance',
  'debtPlan',
  'retirementPlan'
] as const

export type PremiumAdviceModuleType = typeof PREMIUM_ADVICE_MODULES[number]

/**
 * Modules that have upsell pages (subset of premium modules)
 * These are the modules with configured upsell components
 */
export const UPSELL_ADVICE_MODULES = [
  'salarySacrificeContributions',
  'lumpSumConcessionalContributions',
  'personalInsurance',
  'debtPlan',
  'retirementPlan'
] as const

export type UpsellAdviceModuleType = typeof UPSELL_ADVICE_MODULES[number]

/**
 * Modules that require disclaimers to be acknowledged
 */
export const DISCLAIMER_ADVICE_MODULES = [
  'superInvestments',
  'salarySacrificeContributions',
  'lumpSumConcessionalContributions',
  'lumpSumNonConcessionalContributions',
  'personalInsurance',
  'debtPlan',
  'retirementPlan'
] as const

export type DisclaimerAdviceModuleType = typeof DISCLAIMER_ADVICE_MODULES[number]

// =============================================================================
// MODULE STATUS DEFINITIONS
// =============================================================================

/**
 * Standard module status options
 */
export type AdviceModuleStatus =
  | 'infoNeeded'
  | 'ready'
  | 'actioned'
  | 'processing'
  | 'comingSoon'
  | 'onTrack'
  | 'upgrade'

/**
 * Insurance sub-module types (personalInsurance has sub-modules)
 */
export type InsuranceSubModuleType = 'lifeCover' | 'incomeCover' | 'tpdCover' | 'traumaCover'

/**
 * Insurance module status (object with user and partner sub-module statuses)
 */
export type InsuranceAdviceModuleStatus = {
  single: Record<InsuranceSubModuleType, AdviceModuleStatus>
  partner: Record<InsuranceSubModuleType, AdviceModuleStatus>
}

/**
 * Complete module status type mapping
 */
export type AdviceModuleStatusMap = {
  superInvestments: AdviceModuleStatus
  salarySacrificeContributions: AdviceModuleStatus
  lumpSumConcessionalContributions: AdviceModuleStatus
  lumpSumNonConcessionalContributions: AdviceModuleStatus
  personalInsurance: InsuranceAdviceModuleStatus
  debtPlan: AdviceModuleStatus
  retirementPlan: AdviceModuleStatus
}

// =============================================================================
// MODULE CONFIGURATION TYPES
// =============================================================================

/**
 * Basic module configuration
 */
export interface AdviceModuleConfig {
  id: AdviceModuleType
  isPremium: boolean
  hasDisclaimer: boolean
  contentKey?: string
}

/**
 * Upsell module configuration
 */
export interface UpsellModuleConfig {
  title: string
  component: any // Vue component
  button: string
}

/**
 * Route name mapping for modules
 */
export type AdviceModuleRouteMap = {
  superInvestments: 'super-investments'
  salarySacrificeContributions: 'salary-sacrifice-contributions'
  lumpSumConcessionalContributions: 'lump-sum-concessional-contributions'
  lumpSumNonConcessionalContributions: 'lump-sum-non-concessional-contributions'
  personalInsurance: 'personal-insurance'
  debtPlan: 'debt-plan'
  retirementPlan: 'retirement-plan'
}

// =============================================================================
// TYPE GUARD UTILITIES
// =============================================================================

/**
 * Check if a module is premium
 */
export const isPremiumAdviceModule = (module: AdviceModuleType): module is PremiumAdviceModuleType => {
  return PREMIUM_ADVICE_MODULES.includes(module as PremiumAdviceModuleType)
}

/**
 * Check if a module is free
 */
export const isFreeAdviceModule = (module: AdviceModuleType): module is FreeAdviceModuleType => {
  return FREE_ADVICE_MODULES.includes(module as FreeAdviceModuleType)
}

/**
 * Check if a module has upsell functionality
 */
export const hasUpsellAdviceModule = (module: AdviceModuleType): module is UpsellAdviceModuleType => {
  return UPSELL_ADVICE_MODULES.includes(module as UpsellAdviceModuleType)
}

/**
 * Check if a module requires disclaimer acknowledgment
 */
export const requiresDisclaimerAdviceModule = (module: AdviceModuleType): module is DisclaimerAdviceModuleType => {
  return DISCLAIMER_ADVICE_MODULES.includes(module as DisclaimerAdviceModuleType)
}

/**
 * Check if a module is valid
 */
export const isValidAdviceModule = (module: string): module is AdviceModuleType => {
  return ALL_ADVICE_MODULES.includes(module as AdviceModuleType)
}

// =============================================================================
// LEGACY TYPE ALIASES (for gradual migration)
// =============================================================================

/**
 * @deprecated Use AdviceModuleType instead
 * Legacy alias for gradual migration from useAdviceModules
 */
export type LegacyAdviceModuleType = AdviceModuleType

/**
 * @deprecated Use UpsellAdviceModuleType instead
 * Legacy alias for gradual migration from useAdviceUpsell
 */
export type LegacyModuleType = UpsellAdviceModuleType

// =============================================================================
// UTILITY CONSTANTS
// =============================================================================

/**
 * Content key mapping for modules
 */
export const ADVICE_MODULE_CONTENT_KEYS: Record<AdviceModuleType, string> = {
  superInvestments: '[:SUPER_INVESTMENTS:]',
  salarySacrificeContributions: '[:SALARY_SACRIFICE:]',
  lumpSumConcessionalContributions: '[:LUMP_SUM_CONTRIBUTIONS:]',
  lumpSumNonConcessionalContributions: '[:LUMP_SUM_NON_CONCESSIONAL:]',
  personalInsurance: '[:PERSONAL_INSURANCE:]',
  debtPlan: '[:DEBT_OVERVIEW:]',
  retirementPlan: '[:RETIREMENT_PLAN:]'
}

/**
 * Route name mapping for modules
 * NOTE: these seem redundant atm but are here for future updates to standardised route naming
 */
export const ADVICE_MODULE_ROUTE_NAMES: Record<AdviceModuleType, string> = {
  superInvestments: 'superInvestments',
  salarySacrificeContributions: 'salarySacrificeContributions',
  lumpSumConcessionalContributions: 'lumpSumConcessionalContributions',
  lumpSumNonConcessionalContributions: 'lumpSumNonConcessionalContributions',
  personalInsurance: 'personalInsurance',
  debtPlan: 'debtPlan',
  retirementPlan: 'retirementPlan'
}

/**
 * Route slug mapping for modules
 */
export const ADVICE_MODULE_SLUGS: Record<AdviceModuleType, string> = {
  superInvestments: 'super-investments',
  salarySacrificeContributions: 'salary-sacrifice-contributions',
  lumpSumConcessionalContributions: 'lump-sum-concessional-contributions',
  lumpSumNonConcessionalContributions: 'lump-sum-non-concessional-contributions',
  personalInsurance: 'personal-insurance',
  debtPlan: 'debt-plan',
  retirementPlan: 'retirement-plan'
}
